/**
 * octa-ctrl.js — Stroke Controller overlay for the Octa storefront.
 *
 * First activation: visit ?octa-ctrl — panel opens and a toggle button
 * appears in the bottom-right corner on every subsequent visit.
 * To hide the button permanently: click the button's × while panel is closed,
 * or visit ?octa-ctrl-off.
 */
(function () {
  const ACTIVATED_KEY = 'octa-ctrl-on';
  const PRESETS_KEY = 'octa-ctrl-presets';
  const PREVIEW_CLASS = 'octa-ctrl-preview';

  // ?octa-ctrl-off clears the editor UI activation, but the published logo
  // runtime still runs so shoppers keep seeing the live effect.
  const disabledByUrl = location.search.includes('octa-ctrl-off');
  if (disabledByUrl) {
    localStorage.removeItem(ACTIVATED_KEY);
    localStorage.removeItem('octa-ctrl');
  }

  const activatedByUrl = !disabledByUrl && location.search.includes('octa-ctrl');
  const activatedByTuner = window.OctaCtrlOpen === true;
  const activatedByStorage = !disabledByUrl && localStorage.getItem(ACTIVATED_KEY) === '1';
  const editorActive = activatedByUrl || activatedByStorage || activatedByTuner;

  /* ── Default state ─────────────────────────────────────────────────────── */
  const DEFAULTS = {
    speed: 5,
    thickness: 20,
    hueShift: 0,
    saturation: 100,
    glow: 1.2,
    direction: 'normal',
    style: 'worm',
    wormLength: 20,
    position: 'outside',
    blend: 'normal',
    useGradient: false,
    introLogoWidth: 64,
    introHold: 1300,
    introDuration: 900,
    introBackdrop: 90,
    introVideoScale: 108,
    stops: [
      { loc: 0, color: '#ff3300' },
      { loc: 33, color: '#ff9900' },
      { loc: 67, color: '#00ccff' },
      { loc: 100, color: '#ff3300' },
    ],
    paused: false,
  };

  let S = Object.assign({}, DEFAULTS);
  if (editorActive) {
    try {
      const saved = JSON.parse(localStorage.getItem('octa-ctrl'));
      if (saved) S = Object.assign({}, DEFAULTS, saved);
    } catch (_) {}
  }
  if (activatedByTuner) S.paused = false;

  let selectedStop = 0;
  let motionFrame = 0;
  let motionStartedAt = performance.now();
  let motionTravel = 0;

  const BUILT_IN_PRESETS = [
    {
      id: 'edge-trace',
      name: 'Edge Trace',
      locked: true,
      state: {
        speed: 5,
        thickness: 1.2,
        hueShift: 0,
        saturation: 100,
        glow: 1.2,
        direction: 'normal',
        style: 'worm',
        wormLength: 18,
        position: 'outside',
        blend: 'screen',
        useGradient: false,
        stops: DEFAULTS.stops,
      },
    },
    {
      id: 'hot-wire',
      name: 'Hot Wire',
      locked: true,
      state: {
        speed: 3.2,
        thickness: 1,
        hueShift: 18,
        saturation: 100,
        glow: 1.8,
        direction: 'normal',
        style: 'worm',
        wormLength: 24,
        position: 'center',
        blend: 'screen',
        useGradient: true,
        stops: [
          { loc: 0, color: '#ff2a00' },
          { loc: 45, color: '#ffb000' },
          { loc: 100, color: '#ffffff' },
        ],
      },
    },
    {
      id: 'electric-blue',
      name: 'Electric',
      locked: true,
      state: {
        speed: 4.4,
        thickness: 0.9,
        hueShift: 190,
        saturation: 100,
        glow: 2.4,
        direction: 'normal',
        style: 'worm',
        wormLength: 16,
        position: 'outside',
        blend: 'screen',
        useGradient: false,
        stops: [
          { loc: 0, color: '#00e5ff' },
          { loc: 50, color: '#3c4dff' },
          { loc: 100, color: '#ffffff' },
        ],
      },
    },
    {
      id: 'full-ribbon',
      name: 'Ribbon',
      locked: true,
      state: {
        speed: 8,
        thickness: 0.7,
        hueShift: 0,
        saturation: 100,
        glow: 0.6,
        direction: 'normal',
        style: 'continuous',
        wormLength: 90,
        position: 'outside',
        blend: 'normal',
        useGradient: false,
        stops: DEFAULTS.stops,
      },
    },
  ];

  function cloneSettings(source) {
    const base = Object.assign({}, DEFAULTS, source || {});
    base.stops = (base.stops || DEFAULTS.stops).map(stop => ({ ...stop }));
    return base;
  }

  function loadUserPresets() {
    try {
      const saved = JSON.parse(localStorage.getItem(PRESETS_KEY));
      if (Array.isArray(saved)) return saved.map(preset => ({
        id: preset.id || String(Date.now()),
        name: preset.name || 'Saved',
        state: cloneSettings(preset.state),
      }));
    } catch (_) {}
    return [];
  }

  let userPresets = loadUserPresets();

  function saveUserPresets() {
    try { localStorage.setItem(PRESETS_KEY, JSON.stringify(userPresets)); } catch (_) {}
  }

  function allPresets() {
    return [
      ...BUILT_IN_PRESETS.map(preset => ({ ...preset, state: cloneSettings(preset.state) })),
      ...userPresets.map(preset => ({ ...preset, state: cloneSettings(preset.state) })),
    ];
  }

  function gradientCSS(stops) {
    const sorted = [...(stops || DEFAULTS.stops)].sort((a, b) => a.loc - b.loc);
    return `linear-gradient(90deg, ${sorted.map(stop => `${stop.color} ${stop.loc}%`).join(', ')})`;
  }

  function hexToRgb(hex) {
    const clean = String(hex || '#000000').replace('#', '').trim();
    const full = clean.length === 3 ? clean.split('').map(ch => ch + ch).join('') : clean.padEnd(6, '0').slice(0, 6);
    const value = parseInt(full, 16);
    return {
      r: (value >> 16) & 255,
      g: (value >> 8) & 255,
      b: value & 255,
    };
  }

  function rgbToHex({ r, g, b }) {
    return '#' + [r, g, b].map(value => Math.round(value).toString(16).padStart(2, '0')).join('');
  }

  function mixHex(a, b, t) {
    const ca = hexToRgb(a);
    const cb = hexToRgb(b);
    return rgbToHex({
      r: ca.r + (cb.r - ca.r) * t,
      g: ca.g + (cb.g - ca.g) * t,
      b: ca.b + (cb.b - ca.b) * t,
    });
  }

  function sampleStops(stops, loc) {
    const sorted = [...(stops || DEFAULTS.stops)].sort((a, b) => a.loc - b.loc);
    if (!sorted.length) return '#ff3300';
    if (loc <= sorted[0].loc) return sorted[0].color;
    for (let i = 1; i < sorted.length; i++) {
      if (loc <= sorted[i].loc) {
        const prev = sorted[i - 1];
        const next = sorted[i];
        const span = Math.max(1, next.loc - prev.loc);
        return mixHex(prev.color, next.color, (loc - prev.loc) / span);
      }
    }
    return sorted[sorted.length - 1].color;
  }

  function flowColors() {
    return [0, 16, 32, 48, 64, 80, 92, 100].map(loc => sampleStops(S.stops, loc));
  }

  function buildFlowKeyframes(flow) {
    const steps = flow.length;
    return flow.map((_, stopIndex) => {
      const frames = [];
      for (let i = 0; i <= steps; i++) {
        const pct = (i / steps) * 100;
        const color = flow[(stopIndex + i) % steps];
        frames.push(`${pct}% { stop-color: ${color}; }`);
      }
      return `@keyframes octa-flow-stop-${stopIndex + 1} {\n  ${frames.join('\n  ')}\n}`;
    }).join('\n');
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }[char]));
  }

  /* ── CSS injection ─────────────────────────────────────────────────────── */
  const styleEl = document.createElement('style');
  styleEl.id = 'octa-ctrl-css';
  document.head.appendChild(styleEl);

  function buildKeyframes() {
    if (!S.useGradient) return '';
    const sorted = [...S.stops].sort((a, b) => a.loc - b.loc);
    const frames = sorted.map(s => `${s.loc}% { --octa-stroke-color: ${s.color}; }`).join('\n  ');
    return `@keyframes octa-custom-gradient {\n  ${frames}\n}`;
  }

  function traceLength() {
    return S.style === 'worm' ? Math.max(10, Math.min(900, S.wormLength * 10)) : 1000;
  }

  function speedDuration() {
    return Math.max(0.18, 6 / Math.max(0.01, S.speed));
  }

  function formatSpeed(value) {
    return `${Number(value).toFixed(2)}×`;
  }

  function strokeColor() {
    if (S.useGradient) {
      // Use oklch relative-color syntax to bake opacity in
      return `oklch(from var(--octa-stroke-color) l c h / var(--octa-stroke-opacity))`;
    }
    return `hsl(calc(var(--octa-hue) + ${S.hueShift}deg) ${S.saturation}% 50% / var(--octa-stroke-opacity))`;
  }

  function svgAnim() {
    if (S.paused) return 'animation: none;';
    const name  = S.useGradient ? 'octa-custom-gradient' : 'octa-hue-cycle';
    const dir   = S.direction;
    return `animation: ${name} ${speedDuration()}s linear infinite; animation-direction: ${dir};`;
  }

  function wrapperAnim() {
    return '';
  }

  function strokeOpacity() {
    return '1';
  }

  function buildControllerCSS(includePreview) {
    const offset = S.position === 'center'
      ? 'calc(var(--octa-stroke-width) * 0.5)'
      : 'var(--octa-stroke-width)';
    const glow = S.glow + 'px';

    // 8 shadows at 45° intervals — each phase-shifted 45° in hue so the
    // stroke looks like a spinning rainbow gradient around the letterforms.
    function sc(extraDeg) {
      const shifted = S.useGradient
        ? `oklch(from var(--octa-stroke-color) l c h / var(--octa-stroke-opacity))`
        : `hsl(calc(var(--octa-hue) + ${S.hueShift}deg + ${extraDeg}deg) ${S.saturation}% 50% / var(--octa-stroke-opacity))`;
      return shifted;
    }
    const d = `${offset}`;
    const d7 = `calc(${offset} * 0.707)`;
    const nd = `calc(${offset} * -1)`;
    const nd7 = `calc(${offset} * -0.707)`;
    const trace = traceLength();
    const gap = Math.max(0, 1000 - trace);
    const flow = flowColors();
    const ledLength = Math.max(8, Math.min(160, trace / 2.4));
    const ledGap = Math.max(0, 1000 - ledLength);

    const filterRule = `
  filter:
    drop-shadow( ${d}   0    ${glow} ${sc(0)})
    drop-shadow( ${d7}  ${d7}  ${glow} ${sc(45)})
    drop-shadow( 0    ${d}   ${glow} ${sc(90)})
    drop-shadow(${nd7}  ${d7}  ${glow} ${sc(135)})
    drop-shadow(${nd}   0    ${glow} ${sc(180)})
    drop-shadow(${nd7} ${nd7}  ${glow} ${sc(225)})
    drop-shadow( 0   ${nd}   ${glow} ${sc(270)})
    drop-shadow( ${d7} ${nd7}  ${glow} ${sc(315)});
  mix-blend-mode: ${S.blend};`;

    const triggers = [
      '.octa-logo--rgb-on',
      "#header-component[data-sticky-state='active'] .octa-logo--rgb-sticky",
      '.octa-logo--rgb-hover:hover, .octa-logo--rgb-hover:focus-visible',
    ];
    if (includePreview) triggers.push(`.${PREVIEW_CLASS} .octa-logo--rgb`);

    const svgTriggers = triggers.map(t =>
      t.split(',').map(x => x.trim() + ' svg, ' + x.trim() + ' img').join(',\n')
    );

    const wrapperCss = triggers.map(t =>
      `${t} { --octa-stroke-opacity: ${strokeOpacity()}; ${wrapperAnim()} }`
    ).join('\n  ');

    const svgCss = svgTriggers.map(t =>
      `${t} {\n    ${svgAnim()}\n  }`
    ).join('\n  ');

    return `
  /* octa-ctrl live overrides */
  :root {
    --octa-intro-start-width: ${S.introLogoWidth}vw;
    --octa-intro-hold: ${S.introHold}ms;
    --octa-intro-duration: ${S.introDuration}ms;
    --octa-intro-backdrop-opacity: ${S.introBackdrop / 100};
    --octa-intro-video-scale: ${S.introVideoScale / 100};
  }

  html .octa-logo {
    --octa-stroke-width:  ${S.thickness}px;
    --octa-stroke-speed:  ${speedDuration()}s;
    --octa-glow:          ${S.glow}px;
    --octa-blend:         ${S.blend};
    --octa-hue-shift:     ${S.hueShift}deg;
    --octa-saturation:    ${S.saturation}%;
    --octa-trace-length:  ${trace};
    --octa-trace-gap:     ${gap};
    --octa-led-length:    ${ledLength};
    --octa-led-gap:       ${ledGap};
    --octa-flow-1:        ${flow[0]};
    --octa-flow-2:        ${flow[1]};
    --octa-flow-3:        ${flow[2]};
    --octa-flow-4:        ${flow[3]};
    --octa-flow-5:        ${flow[4]};
    --octa-flow-6:        ${flow[5]};
    --octa-flow-7:        ${flow[6]};
    --octa-flow-8:        ${flow[7]};
  }

  ${buildKeyframes()}
  ${buildFlowKeyframes(flow)}

  .octa-logo--rgb img {
    ${filterRule}
  }

  .octa-logo--rgb svg {
    filter: none;
  }

  .octa-logo--rgb .octa-logo__edge--spark path,
  .octa-logo--rgb .octa-logo__edge--glow path {
    animation-play-state: ${S.paused ? 'paused' : 'running'};
    animation-direction: ${S.direction};
  }

  .octa-logo__edge--spark {
    display: none;
  }

  .octa-logo__edge--segment path {
    stroke: var(--octa-segment-color);
    stroke-opacity: var(--octa-stroke-opacity);
    stroke-width: var(--octa-effective-stroke-width);
    stroke-dasharray: var(--octa-led-length) var(--octa-led-gap);
    animation: none;
    filter:
      drop-shadow(0 0 calc(var(--octa-effective-glow) * 0.55) var(--octa-segment-color))
      drop-shadow(0 0 var(--octa-effective-glow) var(--octa-segment-color));
  }

  .octa-logo__edge--segment-lite path {
    filter: drop-shadow(0 0 calc(var(--octa-effective-glow) * 0.45) var(--octa-segment-color));
  }

  .octa-logo__edge--segment-micro path {
    filter: none;
  }

  .octa-edge-flow stop:nth-child(1) { animation: octa-flow-stop-1 ${speedDuration()}s linear infinite; }
  .octa-edge-flow stop:nth-child(2) { animation: octa-flow-stop-2 ${speedDuration()}s linear infinite; }
  .octa-edge-flow stop:nth-child(3) { animation: octa-flow-stop-3 ${speedDuration()}s linear infinite; }
  .octa-edge-flow stop:nth-child(4) { animation: octa-flow-stop-4 ${speedDuration()}s linear infinite; }
  .octa-edge-flow stop:nth-child(5) { animation: octa-flow-stop-5 ${speedDuration()}s linear infinite; }
  .octa-edge-flow stop:nth-child(6) { animation: octa-flow-stop-6 ${speedDuration()}s linear infinite; }
  .octa-edge-flow stop:nth-child(7) { animation: octa-flow-stop-7 ${speedDuration()}s linear infinite; }
  .octa-edge-flow stop:nth-child(8) { animation: octa-flow-stop-8 ${speedDuration()}s linear infinite; }
  .octa-edge-flow stop {
    animation-direction: ${S.direction};
    animation-play-state: ${S.paused ? 'paused' : 'running'};
  }

  ${wrapperCss}
  ${svgCss}
`;
  }

  function applyCSS() {
    installSegmentedEdges();
    updateSegmentedEdges();
    styleEl.textContent = buildControllerCSS(true);
    updateGradientMotion();
  }

  function installSegmentedEdges() {
    document.querySelectorAll('.octa-logo svg').forEach((svg) => {
      const template = svg.querySelector('.octa-logo__edge--spark');
      if (!template) return;
      const segmentCount = segmentBudget(svg);
      if (svg.dataset.octaSegments === '1' && Number(svg.dataset.octaSegmentCount) === segmentCount) return;
      svg.querySelectorAll('.octa-logo__edge--segment').forEach(segment => segment.remove());
      const colors = flowColors();
      for (let i = 0; i < segmentCount; i++) {
        const clone = template.cloneNode(true);
        clone.classList.remove('octa-logo__edge--spark');
        clone.classList.add('octa-logo__edge--segment', `octa-logo__edge--segment-${i + 1}`);
        if (segmentCount <= 3) clone.classList.add('octa-logo__edge--segment-micro');
        else if (segmentCount < 8) clone.classList.add('octa-logo__edge--segment-lite');
        const colorIndex = Math.round((i / segmentCount) * colors.length) % colors.length;
        clone.style.setProperty('--octa-segment-color', colors[colorIndex]);
        clone.style.setProperty('--octa-segment-start', String(i * (-1000 / segmentCount)));
        svg.appendChild(clone);
      }
      svg.dataset.octaSegments = '1';
      svg.dataset.octaSegmentCount = String(segmentCount);
    });
  }

  function segmentBudget(svg) {
    const logo = svg.closest('.octa-logo');
    if (!logo) return 3;
    if (logo.classList.contains('octa-logo-tuner__logo--hero')) return 8;
    if (logo.closest('#header-component') || logo.classList.contains('octa-logo-tuner__logo--header-live')) return 4;
    if (logo.classList.contains('octa-logo-tuner__nav-logo')) return 4;
    if (logo.classList.contains('octa-logo-tuner__logo')) return 3;
    return 4;
  }

  function updateSegmentedEdges() {
    const colors = flowColors();
    document.querySelectorAll('.octa-logo svg').forEach((svg) => {
      const segments = Array.from(svg.querySelectorAll('.octa-logo__edge--segment'));
      const count = Math.max(1, segments.length);
      segments.forEach((segment, index) => {
        const colorIndex = Math.round((index / count) * colors.length) % colors.length;
        const start = index * (-1000 / count);
        segment.style.setProperty('--octa-segment-color', colors[colorIndex]);
        segment.style.setProperty('--octa-segment-start', String(start));
        configureSegmentMotion(segment, start);
      });
    });
  }

  function configureSegmentMotion(segment, start) {
    segment.dataset.octaSegmentStart = String(start);
    segment.querySelectorAll('path').forEach((path) => {
      path.style.removeProperty('stroke-dashoffset');
      path.setAttribute('stroke-dashoffset', String(start));
      path.querySelectorAll('animate[data-octa-motion]').forEach(animation => animation.remove());
    });
  }

  function updateMotionOffsets(now) {
    const duration = Math.max(180, speedDuration() * 1000);
    const direction = S.direction === 'reverse' ? 1 : -1;
    if (!S.paused) {
      const elapsed = (now - motionStartedAt) % duration;
      motionTravel = (elapsed / duration) * 1000 * direction;
    }

    document.querySelectorAll('.octa-logo__edge--segment').forEach((segment) => {
      const start = Number(segment.dataset.octaSegmentStart || 0);
      segment.querySelectorAll('path').forEach((path) => {
        path.setAttribute('stroke-dashoffset', String(start + motionTravel));
      });
    });
  }

  function tickMotion(now) {
    updateMotionOffsets(now);
    motionFrame = requestAnimationFrame(tickMotion);
  }

  function restartMotionTicker() {
    if (!motionFrame) motionFrame = requestAnimationFrame(tickMotion);
    updateMotionOffsets(performance.now());
  }

  function updateGradientMotion() {
    document.querySelectorAll('.octa-edge-flow__motion').forEach((motion) => {
      motion.setAttribute('dur', `${speedDuration()}s`);
      motion.setAttribute(
        'values',
        S.direction === 'reverse'
          ? '317.6 0;0 0;-317.6 0'
          : '-317.6 0;0 0;317.6 0'
      );
      if (S.paused) {
        motion.setAttribute('dur', '999999s');
      }
    });
    restartMotionTicker();
  }

  function save() {
    try { localStorage.setItem('octa-ctrl', JSON.stringify(S)); } catch (_) {}
  }

  function update() { applyCSS(); save(); }

  function setPaused(paused) {
    const nextPaused = Boolean(paused);
    if (nextPaused && !S.paused) {
      updateMotionOffsets(performance.now());
    }
    if (!nextPaused && S.paused) {
      const duration = Math.max(180, speedDuration() * 1000);
      motionStartedAt = performance.now() - (Math.abs(motionTravel) / 1000) * duration;
    }
    S.paused = Boolean(paused);
    const pauseButton = document.querySelector('#ocp-pause');
    if (pauseButton) pauseButton.textContent = S.paused ? 'RESUME' : 'PAUSE';
    update();
    document.dispatchEvent(new CustomEvent('octa-ctrl-state', { detail: { paused: S.paused } }));
    return S.paused;
  }

  function togglePaused() {
    return setPaused(!S.paused);
  }

  function validateLogoTrace() {
    installSegmentedEdges();
    updateSegmentedEdges();

    const logos = Array.from(document.querySelectorAll('.octa-logo--rgb svg'));
    const segmentGroups = Array.from(document.querySelectorAll('.octa-logo__edge--segment'));
    const segmentPaths = Array.from(document.querySelectorAll('.octa-logo__edge--segment path'));
    const colors = new Set(segmentGroups.map(group => group.style.getPropertyValue('--octa-segment-color').trim()).filter(Boolean));
    const animated = motionFrame && !S.paused ? segmentPaths.length : 0;
    const heroPath = document.querySelector('.octa-logo-tuner__logo--hero .octa-logo__edge--segment path');
    const headerPath = document.querySelector('.octa-logo-tuner__nav-logo .octa-logo__edge--segment path') ||
      document.querySelector('.octa-logo-tuner__size .octa-logo__edge--segment path');
    const liveHeaderPath = document.querySelector('#header-component .header-logo .octa-logo__edge--segment path');
    const heroStroke = heroPath ? parseFloat(getComputedStyle(heroPath).strokeWidth) : 0;
    const headerStroke = headerPath ? parseFloat(getComputedStyle(headerPath).strokeWidth) : 0;
    const liveHeaderStroke = liveHeaderPath ? parseFloat(getComputedStyle(liveHeaderPath).strokeWidth) : 0;

    const checks = [
      { id: 'logos', label: 'RGB logos rendered', pass: logos.length > 0, value: String(logos.length) },
      { id: 'segments', label: 'LED edge segments installed', pass: segmentGroups.length >= logos.length * 3, value: `${segmentGroups.length}/${logos.length * 3}+` },
      { id: 'colors', label: 'Different colors around edge', pass: colors.size >= 4, value: `${colors.size} colors` },
      { id: 'motion', label: 'Edge animations running', pass: S.paused || animated > 0, value: S.paused ? 'paused' : `${animated} paths` },
      { id: 'scale', label: 'Small logo stroke scales down', pass: !heroStroke || !headerStroke || headerStroke <= heroStroke, value: heroStroke && headerStroke ? `${headerStroke.toFixed(2)}px / ${heroStroke.toFixed(2)}px` : 'n/a' },
      { id: 'header', label: 'Live header stroke scales safely', pass: !liveHeaderStroke || !heroStroke || liveHeaderStroke < heroStroke, value: liveHeaderStroke ? `${liveHeaderStroke.toFixed(2)}px` : 'n/a' },
      { id: 'speed', label: 'Fine speed control available', pass: Number(document.querySelector('#ocp-speed')?.min) <= 0.01, value: document.querySelector('#ocp-speed')?.min || 'missing' },
      { id: 'thickness', label: 'Thickness reaches 20', pass: Number(document.querySelector('#ocp-thick')?.max) >= 20, value: document.querySelector('#ocp-thick')?.max || 'missing' },
    ];

    return {
      pass: checks.every(check => check.pass),
      paused: S.paused,
      speed: S.speed,
      duration: speedDuration(),
      checks,
    };
  }

  function setPreview(active) {
    document.documentElement.classList.toggle(PREVIEW_CLASS, active);
  }

  function exportCSS() {
    return `/* Paste into assets/octa-logo.css after the base Octa logo rules. */\n${buildControllerCSS(false)}`;
  }

  function publishPayload() {
    return {
      type: 'octa-logo-tune',
      version: 1,
      generatedAt: new Date().toISOString(),
      sourceUrl: location.href,
      state: cloneSettings(S),
    };
  }

  function publishPayloadText() {
    return JSON.stringify(publishPayload(), null, 2);
  }

  applyCSS();

  window.OctaCtrl = {
    getState: () => cloneSettings(S),
    getPublishPayload: publishPayload,
    getPublishPayloadText: publishPayloadText,
    setPaused,
    togglePaused,
    validate: validateLogoTrace,
  };

  if (!editorActive) return;

  // Mark as activated so the button shows on future editor visits.
  localStorage.setItem(ACTIVATED_KEY, '1');

  /* ── Toggle button ─────────────────────────────────────────────────────── */
  let panelVisible = activatedByUrl || activatedByTuner; // auto-open on first ?octa-ctrl visit or tuner page

  const toggleBtn = document.createElement('button');
  toggleBtn.id = 'octa-ctrl-toggle';
  toggleBtn.title = 'Stroke Controller';
  toggleBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18"><circle cx="12" cy="12" r="3"/><path d="M12 2v2m0 16v2M4.22 4.22l1.42 1.42m12.72 12.72 1.42 1.42M2 12h2m16 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>`;

  const toggleStyle = document.createElement('style');
  toggleStyle.textContent = `
#octa-ctrl-toggle {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(17,17,17,0.92);
  border: 1px solid #444;
  color: #ccc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0,0,0,.5);
  z-index: 99998;
  transition: background .15s, transform .15s;
  backdrop-filter: blur(8px);
}
#octa-ctrl-toggle:hover { background: rgba(40,40,40,0.95); color: #fff; transform: scale(1.08); }
#octa-ctrl-toggle.active { background: rgba(50,50,50,0.97); color: #fff; border-color: #666; }
`;
  document.head.appendChild(toggleStyle);
  document.body.appendChild(toggleBtn);

  toggleBtn.addEventListener('click', () => {
    panelVisible = !panelVisible;
    panel.style.display = panelVisible ? 'block' : 'none';
    toggleBtn.classList.toggle('active', panelVisible);
    setPreview(panelVisible);
  });

  /* ── Panel HTML ────────────────────────────────────────────────────────── */
  const panel = document.createElement('div');
  panel.id = 'octa-ctrl-panel';
  panel.style.display = panelVisible ? 'block' : 'none';
  panel.innerHTML = /* html */ `
<div id="ocp-header">
  <span>STROKE CONTROLLER</span>
  <div style="display:flex;gap:6px;">
    <span class="ocp-dot" style="background:#ff5f57"></span>
    <span class="ocp-dot" style="background:#febc2e"></span>
    <span class="ocp-dot" style="background:#28c840"></span>
  </div>
</div>

<div class="ocp-row"><label>SPEED</label><input class="ocp-value-bubble" id="ocp-speed-val" type="number" min="0.01" max="30" step="0.01" value="${S.speed}"></div>
<input id="ocp-speed" type="range" min="0.01" max="30" step="0.01" value="${S.speed}">

<div class="ocp-row"><label>THICKNESS</label><input class="ocp-value-bubble" id="ocp-thick-val" type="number" min="0.1" max="20" step="0.1" value="${S.thickness}"></div>
<input id="ocp-thick" type="range" min="0.1" max="20" step="0.1" value="${S.thickness}">

<div class="ocp-row"><label>HUE SHIFT</label><input class="ocp-value-bubble" id="ocp-hue-val" type="number" min="0" max="360" step="5" value="${S.hueShift}"></div>
<input id="ocp-hue" type="range" min="0" max="360" step="5" value="${S.hueShift}">

<div class="ocp-row"><label>SATURATION</label><input class="ocp-value-bubble" id="ocp-sat-val" type="number" min="0" max="100" step="5" value="${S.saturation}"></div>
<input id="ocp-sat" type="range" min="0" max="100" step="5" value="${S.saturation}">

<div class="ocp-row"><label>GLOW</label><input class="ocp-value-bubble" id="ocp-glow-val" type="number" min="0" max="4" step="0.1" value="${S.glow}"></div>
<input id="ocp-glow" type="range" min="0" max="4" step="0.1" value="${S.glow}">

<div class="ocp-section-label">DIRECTION</div>
<div class="ocp-toggle-row">
  <button class="ocp-btn${S.direction==='normal'?' ocp-active':''}" data-dir="normal">Forward</button>
  <button class="ocp-btn${S.direction==='reverse'?' ocp-active':''}" data-dir="reverse">Reverse</button>
</div>

<div class="ocp-section-label">STYLE</div>
<div class="ocp-toggle-row">
  <button class="ocp-btn${S.style==='worm'?' ocp-active':''}" data-style="worm">Worm</button>
  <button class="ocp-btn${S.style==='continuous'?' ocp-active':''}" data-style="continuous">Continuous</button>
</div>

<div class="ocp-row"><label>TRACE LENGTH</label><input class="ocp-value-bubble" id="ocp-worm-val" type="number" min="10" max="90" step="5" value="${S.wormLength}"></div>
<input id="ocp-worm" type="range" min="10" max="90" step="5" value="${S.wormLength}">

<div class="ocp-section-label">POSITION</div>
<div class="ocp-toggle-row">
  <button class="ocp-btn${S.position==='outside'?' ocp-active':''}" data-pos="outside">Outside</button>
  <button class="ocp-btn${S.position==='center'?' ocp-active':''}" data-pos="center">Center</button>
</div>

<div class="ocp-section-label">BLEND</div>
<div class="ocp-toggle-row">
  <button class="ocp-btn${S.blend==='screen'?' ocp-active':''}" data-blend="screen">Screen</button>
  <button class="ocp-btn${S.blend==='normal'?' ocp-active':''}" data-blend="normal">Normal</button>
  <button class="ocp-btn${S.blend==='overlay'?' ocp-active':''}" data-blend="overlay">Overlay</button>
</div>

<div class="ocp-section-label">GRADIENT</div>
<div id="ocp-grad-bar-wrap">
  <canvas id="ocp-grad-canvas" height="36"></canvas>
  <div id="ocp-stops-row"></div>
</div>
<div class="ocp-stop-editor" id="ocp-stop-editor">
  <input type="color" id="ocp-stop-color">
  <label>LOC</label>
  <input type="number" id="ocp-stop-loc" min="0" max="100" step="1">
  <span>%</span>
  <button id="ocp-stop-remove">✕</button>
</div>
<div class="ocp-hint">Click bar to add · Drag to move · Dbl-click stop to remove</div>

<div class="ocp-section-label" style="display:flex;align-items:center;gap:8px;">
  GRADIENT
  <label style="font-size:10px;font-weight:400;color:#888;display:flex;align-items:center;gap:4px;cursor:pointer;">
    <input type="checkbox" id="ocp-use-grad" ${S.useGradient?'checked':''}>
    use custom
  </label>
</div>

<div class="ocp-section-label">SAVED</div>
<div id="ocp-presets" aria-label="Saved logo presets"></div>
<div id="ocp-preset-actions">
  <button id="ocp-save-current" type="button">+ SAVE CURRENT</button>
  <button id="ocp-publish-current" type="button">PUBLISH CURRENT TUNE</button>
</div>

<div class="ocp-section-label">LANDING</div>

<div class="ocp-row"><label>LOGO WIDTH</label><input class="ocp-value-bubble" id="ocp-intro-width-val" type="number" min="32" max="88" step="1" value="${S.introLogoWidth}"></div>
<input id="ocp-intro-width" type="range" min="32" max="88" step="1" value="${S.introLogoWidth}">

<div class="ocp-row"><label>HOLD</label><input class="ocp-value-bubble" id="ocp-intro-hold-val" type="number" min="0" max="4000" step="100" value="${S.introHold}"></div>
<input id="ocp-intro-hold" type="range" min="0" max="4000" step="100" value="${S.introHold}">

<div class="ocp-row"><label>FLY TIME</label><input class="ocp-value-bubble" id="ocp-intro-duration-val" type="number" min="300" max="2000" step="50" value="${S.introDuration}"></div>
<input id="ocp-intro-duration" type="range" min="300" max="2000" step="50" value="${S.introDuration}">

<div class="ocp-row"><label>BACKDROP</label><input class="ocp-value-bubble" id="ocp-intro-backdrop-val" type="number" min="0" max="100" step="5" value="${S.introBackdrop}"></div>
<input id="ocp-intro-backdrop" type="range" min="0" max="100" step="5" value="${S.introBackdrop}">

<div class="ocp-row"><label>VIDEO ZOOM</label><input class="ocp-value-bubble" id="ocp-intro-video-scale-val" type="number" min="100" max="160" step="1" value="${S.introVideoScale}"></div>
<input id="ocp-intro-video-scale" type="range" min="100" max="160" step="1" value="${S.introVideoScale}">

<div id="ocp-intro-actions">
  <button id="ocp-replay-intro">REPLAY INTRO</button>
</div>

<div id="ocp-footer">
  <button id="ocp-pause">${S.paused?'RESUME':'PAUSE'}</button>
  <button id="ocp-copy">COPY CSS</button>
  <button id="ocp-reset">RESET</button>
</div>
<div id="ocp-status" aria-live="polite"></div>
<textarea id="ocp-export" readonly spellcheck="false"></textarea>
`;

  /* ── Panel styles ──────────────────────────────────────────────────────── */
  const panelStyle = document.createElement('style');
  panelStyle.textContent = `
#octa-ctrl-panel {
  position: fixed;
  top: 80px;
  right: 16px;
  width: 300px;
  background: #111;
  color: #ddd;
  font: 11px/1.4 'SF Mono', 'Menlo', monospace;
  letter-spacing: .06em;
  border-radius: 10px;
  padding: 0 0 12px;
  box-shadow: 0 8px 40px rgba(0,0,0,.8);
  z-index: 99999;
  user-select: none;
  max-height: calc(100vh - 96px);
  overflow-y: auto;
}
#ocp-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 14px 8px;
  border-bottom: 1px solid #222;
  font-size: 12px; font-weight: 600; color: #fff;
}
.ocp-dot { width: 12px; height: 12px; border-radius: 50%; display:inline-block; }
#octa-ctrl-panel input[type=range] {
  width: calc(100% - 28px);
  margin: 0 14px 10px;
  display: block;
  accent-color: #555;
  height: 2px;
  cursor: pointer;
}
#octa-ctrl-panel input[type=range]::-webkit-slider-runnable-track {
  height: 2px;
  border-radius: 999px;
  background: #3a3a3a;
}
#octa-ctrl-panel input[type=range]::-webkit-slider-thumb {
  margin-top: -5px;
}
#octa-ctrl-panel input[type=range]::-moz-range-track {
  height: 2px;
  border-radius: 999px;
  background: #3a3a3a;
}
.ocp-row {
  display: flex; justify-content: space-between;
  padding: 8px 14px 2px;
  color: #aaa; font-size: 10px; text-transform: uppercase;
  align-items: center;
}
.ocp-row label { color: #888; }
.ocp-value-bubble {
  width: 62px;
  min-height: 22px;
  border: 1px solid #2e2e32;
  border-radius: 999px;
  background: #1d1d20;
  color: #ddd;
  font: 10px/1 'SF Mono','Menlo',monospace;
  letter-spacing: .02em;
  text-align: right;
  padding: 3px 8px;
  outline: none;
  opacity: .72;
  transition: opacity .15s, border-color .15s, background .15s, box-shadow .15s;
}
.ocp-value-bubble:focus,
.ocp-value-bubble.ocp-active-value {
  opacity: 1;
  background: #2a2a2d;
  border-color: #666;
  box-shadow: 0 0 0 2px rgb(255 255 255 / .08);
}
.ocp-value-bubble::-webkit-inner-spin-button,
.ocp-value-bubble::-webkit-outer-spin-button {
  opacity: .65;
}
.ocp-section-label {
  padding: 10px 14px 4px;
  color: #888; font-size: 10px; text-transform: uppercase;
}
.ocp-toggle-row {
  display: flex; gap: 4px;
  padding: 0 14px 6px;
}
.ocp-btn {
  flex: 1; padding: 6px 0;
  background: #222; border: 1px solid #333; border-radius: 6px;
  color: #888; font: 10px/1 'SF Mono','Menlo',monospace;
  cursor: pointer; transition: all .15s;
}
.ocp-btn:hover { background: #2a2a2a; color: #ccc; }
.ocp-btn.ocp-active { background: #333; border-color: #555; color: #fff; }
#ocp-grad-bar-wrap {
  margin: 0 14px 6px;
  border-radius: 6px;
  overflow: visible;
}
#ocp-grad-canvas {
  width: 100%; display: block;
  border-radius: 6px 6px 0 0;
  cursor: crosshair;
}
#ocp-stops-row {
  position: relative;
  height: 20px;
  background: #1a1a1a;
  border-radius: 0 0 6px 6px;
}
.ocp-stop-handle {
  position: absolute;
  width: 14px; height: 14px;
  border-radius: 3px;
  border: 2px solid #fff;
  top: 3px;
  transform: translateX(-50%);
  cursor: grab;
  box-shadow: 0 1px 4px rgba(0,0,0,.6);
  transition: border-color .1s;
}
.ocp-stop-handle.selected { border-color: #fff; box-shadow: 0 0 0 2px #555; }
.ocp-stop-editor {
  display: flex; align-items: center; gap: 6px;
  padding: 4px 14px 2px;
}
.ocp-stop-editor input[type=color] {
  width: 32px; height: 28px;
  border: none; border-radius: 4px;
  cursor: pointer; padding: 0; margin: 0;
}
.ocp-stop-editor label { color: #888; font-size: 10px; }
.ocp-stop-editor input[type=number] {
  width: 44px;
  background: #222; border: 1px solid #333; border-radius: 4px;
  color: #ddd; font: 11px 'SF Mono','Menlo',monospace;
  padding: 3px 5px; text-align: center;
}
.ocp-stop-editor span { color: #666; font-size: 10px; }
#ocp-stop-remove {
  margin-left: auto;
  background: #222; border: 1px solid #333; border-radius: 4px;
  color: #888; cursor: pointer; padding: 3px 7px; font-size: 11px;
}
#ocp-stop-remove:hover { color: #fff; border-color: #555; }
.ocp-hint {
  padding: 2px 14px 8px;
  color: #555; font-size: 9px; text-align: center;
}
#ocp-presets {
  display: grid;
  gap: 6px;
  padding: 0 14px 6px;
}
.ocp-preset {
  display: grid;
  grid-template-columns: 42px 1fr auto;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  padding: 5px 6px;
  background: #1b1b1d;
  border: 1px solid #2c2c30;
  border-radius: 6px;
  color: #aaa;
  cursor: pointer;
  text-align: left;
}
.ocp-preset:hover {
  background: #242428;
  color: #fff;
}
.ocp-preset-swatch {
  height: 18px;
  border-radius: 4px;
  box-shadow: inset 0 0 0 1px rgb(255 255 255 / 0.18);
}
.ocp-preset-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font: 10px/1.2 'SF Mono','Menlo',monospace;
  letter-spacing: .04em;
}
.ocp-preset-meta {
  color: #666;
  font-size: 9px;
}
.ocp-preset-delete {
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
  background: #222;
  border: 1px solid #333;
  border-radius: 4px;
  color: #777;
  cursor: pointer;
}
.ocp-preset-delete:hover {
  color: #fff;
  border-color: #555;
}
#ocp-preset-actions {
  display: grid;
  gap: 6px;
  padding: 0 14px 8px;
}
#ocp-preset-actions button {
  width: 100%;
  padding: 8px;
  background: #1e1e1e;
  border: 1px solid #333;
  border-radius: 6px;
  color: #aaa;
  font: 11px/1 'SF Mono','Menlo',monospace;
  cursor: pointer;
  letter-spacing: .06em;
  transition: all .15s;
}
#ocp-preset-actions button:hover { background: #282828; color: #fff; }
#ocp-publish-current {
  color: #fff !important;
  background: #303030 !important;
  border-color: #5a5a5a !important;
}
#ocp-publish-current:hover {
  background: #3a3a3a !important;
  border-color: #777 !important;
}
#ocp-intro-actions {
  padding: 2px 14px 10px;
}
#ocp-intro-actions button {
  width: 100%;
  padding: 8px;
  background: #1e1e1e;
  border: 1px solid #333;
  border-radius: 6px;
  color: #aaa;
  font: 11px/1 'SF Mono','Menlo',monospace;
  cursor: pointer;
  letter-spacing: .06em;
  transition: all .15s;
}
#ocp-intro-actions button:hover { background: #282828; color: #fff; }
#ocp-footer {
  display: flex; gap: 8px;
  padding: 10px 14px 0;
  border-top: 1px solid #1e1e1e;
  margin-top: 4px;
}
#ocp-footer button {
  flex: 1; padding: 8px;
  background: #1e1e1e; border: 1px solid #333; border-radius: 6px;
  color: #aaa; font: 11px/1 'SF Mono','Menlo',monospace;
  cursor: pointer; letter-spacing: .06em;
  transition: all .15s;
}
#ocp-footer button:hover { background: #282828; color: #fff; }
#ocp-status {
  min-height: 14px;
  padding: 8px 14px 0;
  color: #777;
  font-size: 9px;
  text-align: center;
}
#ocp-export {
  display: none;
  width: calc(100% - 28px);
  height: 120px;
  margin: 8px 14px 0;
  padding: 8px;
  background: #080808;
  border: 1px solid #333;
  border-radius: 6px;
  color: #bbb;
  font: 9px/1.35 'SF Mono','Menlo',monospace;
  resize: vertical;
  white-space: pre;
}
`;
  document.head.appendChild(panelStyle);
  document.body.appendChild(panel);

  /* ── Gradient canvas ───────────────────────────────────────────────────── */
  const canvas = panel.querySelector('#ocp-grad-canvas');
  const stopsRow = panel.querySelector('#ocp-stops-row');

  function drawGradient() {
    const ctx = canvas.getContext('2d');
    const W = canvas.offsetWidth || 272;
    canvas.width = W;
    const sorted = [...S.stops].sort((a, b) => a.loc - b.loc);
    const grad = ctx.createLinearGradient(0, 0, W, 0);
    sorted.forEach(s => grad.addColorStop(Math.min(1, Math.max(0, s.loc / 100)), s.color));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, 36);
  }

  function renderStops() {
    stopsRow.innerHTML = '';
    S.stops.forEach((stop, i) => {
      const el = document.createElement('div');
      el.className = 'ocp-stop-handle' + (i === selectedStop ? ' selected' : '');
      el.style.left = stop.loc + '%';
      el.style.background = stop.color;
      el.dataset.idx = i;

      // Drag
      el.addEventListener('mousedown', e => {
        e.preventDefault();
        const startX = e.clientX;
        const startLoc = stop.loc;
        const barW = stopsRow.offsetWidth;
        function onMove(ev) {
          const dx = ev.clientX - startX;
          S.stops[i].loc = Math.min(100, Math.max(0, startLoc + (dx / barW) * 100));
          drawGradient();
          renderStops();
          update();
        }
        function onUp() {
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onUp);
        }
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
      });

      // Select
      el.addEventListener('click', e => { e.stopPropagation(); selectStop(i); });

      // Remove on double-click
      el.addEventListener('dblclick', e => { e.stopPropagation(); removeStop(i); });

      stopsRow.appendChild(el);
    });
    updateStopEditor();
  }

  // Click gradient bar to add stop
  canvas.addEventListener('click', e => {
    const rect = canvas.getBoundingClientRect();
    const loc = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    // Sample color from canvas
    const ctx = canvas.getContext('2d');
    const px = ctx.getImageData(e.clientX - rect.left, 18, 1, 1).data;
    const hex = '#' + [px[0],px[1],px[2]].map(v => v.toString(16).padStart(2,'0')).join('');
    S.stops.push({ loc, color: hex });
    selectedStop = S.stops.length - 1;
    drawGradient(); renderStops(); update();
  });

  function selectStop(i) {
    selectedStop = i;
    renderStops();
  }

  function removeStop(i) {
    if (S.stops.length <= 2) return;
    S.stops.splice(i, 1);
    selectedStop = Math.min(selectedStop, S.stops.length - 1);
    drawGradient(); renderStops(); update();
  }

  function applyPreset(preset) {
    S = cloneSettings(preset.state);
    selectedStop = 0;
    rebuildPanel();
    drawGradient();
    renderStops();
    update();
    panel.querySelector('#ocp-status').textContent = `Loaded ${preset.name}`;
  }

  function renderPresets() {
    const wrap = panel.querySelector('#ocp-presets');
    if (!wrap) return;
    wrap.innerHTML = '';

    allPresets().forEach((preset) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'ocp-preset';
      item.title = `Load ${preset.name}`;
      item.innerHTML = `
        <span class="ocp-preset-swatch"></span>
        <span>
          <span class="ocp-preset-name">${escapeHtml(preset.name)}</span>
          <span class="ocp-preset-meta">${preset.state.style || 'worm'} · ${preset.state.wormLength || 0}%</span>
        </span>
        ${preset.locked ? '<span class="ocp-preset-meta">base</span>' : '<span class="ocp-preset-delete" aria-label="Delete preset">×</span>'}
      `;
      item.querySelector('.ocp-preset-swatch').style.background = preset.state.useGradient
        ? gradientCSS(preset.state.stops)
        : `linear-gradient(90deg, hsl(${preset.state.hueShift || 0}deg 100% 50%), hsl(${((preset.state.hueShift || 0) + 120) % 360}deg 100% 50%), hsl(${((preset.state.hueShift || 0) + 240) % 360}deg 100% 50%))`;

      item.addEventListener('click', () => applyPreset(preset));

      const del = item.querySelector('.ocp-preset-delete');
      if (del) {
        del.addEventListener('click', (event) => {
          event.stopPropagation();
          userPresets = userPresets.filter(userPreset => userPreset.id !== preset.id);
          saveUserPresets();
          renderPresets();
          panel.querySelector('#ocp-status').textContent = `Deleted ${preset.name}`;
        });
      }

      wrap.appendChild(item);
    });
  }

  function updateStopEditor() {
    const stop = S.stops[selectedStop];
    if (!stop) return;
    panel.querySelector('#ocp-stop-color').value = stop.color;
    panel.querySelector('#ocp-stop-loc').value = Math.round(stop.loc);
  }

  panel.querySelector('#ocp-stop-color').addEventListener('input', e => {
    if (!S.stops[selectedStop]) return;
    S.stops[selectedStop].color = e.target.value;
    drawGradient(); renderStops(); update();
  });

  panel.querySelector('#ocp-stop-loc').addEventListener('input', e => {
    if (!S.stops[selectedStop]) return;
    S.stops[selectedStop].loc = parseFloat(e.target.value) || 0;
    drawGradient(); renderStops(); update();
  });

  panel.querySelector('#ocp-stop-remove').addEventListener('click', () => removeStop(selectedStop));

  /* ── Sliders ───────────────────────────────────────────────────────────── */
  function slider(id, valId, key, fmt, rawFmt) {
    const el = panel.querySelector('#' + id);
    const vl = panel.querySelector('#' + valId);
    const min = Number(el.min);
    const max = Number(el.max);
    const step = Number(el.step) || 1;
    const raw = rawFmt || (v => String(v));
    let bubbleTimer;

    function normalize(value) {
      let next = Number(value);
      if (!Number.isFinite(next)) next = S[key];
      next = Math.max(min, Math.min(max, next));
      const precision = String(step).includes('.') ? String(step).split('.')[1].length : 0;
      return Number(next.toFixed(precision));
    }

    function showBubble() {
      clearTimeout(bubbleTimer);
      vl.classList.add('ocp-active-value');
      bubbleTimer = setTimeout(() => vl.classList.remove('ocp-active-value'), 900);
    }

    function commit(value, fromInput) {
      const typed = Number(value);
      if (fromInput && !Number.isFinite(typed)) return;
      S[key] = normalize(value);
      el.value = raw(S[key]);
      if (!fromInput) vl.value = raw(S[key]);
      update();
      showBubble();
    }

    el.addEventListener('input', () => commit(el.value, false));
    el.addEventListener('pointerdown', showBubble);
    el.addEventListener('focus', showBubble);
    vl.addEventListener('input', () => commit(vl.value, true));
    vl.addEventListener('change', () => commit(vl.value, false));
    vl.addEventListener('blur', () => commit(vl.value, false));
    vl.addEventListener('focus', () => { showBubble(); vl.select(); });
  }

  slider('ocp-speed', 'ocp-speed-val', 'speed', formatSpeed, v => v.toFixed(2));
  slider('ocp-thick', 'ocp-thick-val', 'thickness', v => v.toFixed(1), v => v.toFixed(1));
  slider('ocp-hue', 'ocp-hue-val', 'hueShift', v => v + '°');
  slider('ocp-sat', 'ocp-sat-val', 'saturation', v => v + '%');
  slider('ocp-glow', 'ocp-glow-val', 'glow', v => v.toFixed(1), v => v.toFixed(1));
  slider('ocp-worm', 'ocp-worm-val', 'wormLength', v => v + '%');
  slider('ocp-intro-width', 'ocp-intro-width-val', 'introLogoWidth', v => v + 'vw');
  slider('ocp-intro-hold', 'ocp-intro-hold-val', 'introHold', v => v + 'ms');
  slider('ocp-intro-duration', 'ocp-intro-duration-val', 'introDuration', v => v + 'ms');
  slider('ocp-intro-backdrop', 'ocp-intro-backdrop-val', 'introBackdrop', v => v + '%');
  slider('ocp-intro-video-scale', 'ocp-intro-video-scale-val', 'introVideoScale', v => v + '%');

  /* ── Toggle buttons ────────────────────────────────────────────────────── */
  function toggleBtns(selector, key, attr) {
    panel.querySelectorAll(selector).forEach(btn => {
      btn.addEventListener('click', () => {
        S[key] = btn.dataset[attr];
        panel.querySelectorAll(selector).forEach(b => b.classList.toggle('ocp-active', b === btn));
        update();
      });
    });
  }
  toggleBtns('[data-dir]',   'direction', 'dir');
  toggleBtns('[data-style]', 'style',     'style');
  toggleBtns('[data-pos]',   'position',  'pos');
  toggleBtns('[data-blend]', 'blend',     'blend');

  /* ── Gradient toggle ───────────────────────────────────────────────────── */
  panel.querySelector('#ocp-use-grad').addEventListener('change', e => {
    S.useGradient = e.target.checked;
    update();
  });

  panel.querySelector('#ocp-save-current').addEventListener('click', () => {
    const fallback = `Preset ${userPresets.length + 1}`;
    const name = window.prompt('Name this logo preset', fallback);
    if (name === null) return;
    const cleanName = name.trim() || fallback;
    userPresets.push({
      id: String(Date.now()),
      name: cleanName,
      state: cloneSettings(S),
    });
    saveUserPresets();
    renderPresets();
    panel.querySelector('#ocp-status').textContent = `Saved ${cleanName}`;
  });

  panel.querySelector('#ocp-publish-current').addEventListener('click', async () => {
    const status = panel.querySelector('#ocp-status');
    const exportBox = panel.querySelector('#ocp-export');
    const payload = publishPayloadText();
    try { localStorage.setItem('octa-ctrl-publish-payload', payload); } catch (_) {}

    try {
      await navigator.clipboard.writeText(payload);
      exportBox.style.display = 'none';
      status.textContent = 'Publish tune copied';
    } catch (_) {
      exportBox.value = payload;
      exportBox.style.display = 'block';
      exportBox.focus();
      exportBox.select();
      status.textContent = 'Publish tune ready below';
    }
  });

  panel.querySelector('#ocp-replay-intro').addEventListener('click', () => {
    window.OctaIntro?.replay?.();
  });

  /* ── Pause / Reset ─────────────────────────────────────────────────────── */
  panel.querySelector('#ocp-pause').addEventListener('click', function () {
    setPaused(!S.paused);
  });

  panel.querySelector('#ocp-copy').addEventListener('click', async function () {
    const status = panel.querySelector('#ocp-status');
    const exportBox = panel.querySelector('#ocp-export');
    const css = exportCSS();
    try {
      await navigator.clipboard.writeText(css);
      exportBox.style.display = 'none';
      status.textContent = 'CSS copied';
    } catch (_) {
      exportBox.value = css;
      exportBox.style.display = 'block';
      exportBox.focus();
      exportBox.select();
      status.textContent = 'CSS ready below';
    }
  });

  panel.querySelector('#ocp-reset').addEventListener('click', () => {
    Object.assign(S, DEFAULTS);
    S.stops = DEFAULTS.stops.map(s => ({...s}));
    rebuildPanel();
    renderPresets();
    update();
  });

  /* ── Draggable panel ───────────────────────────────────────────────────── */
  const header = panel.querySelector('#ocp-header');
  let dragX = 0, dragY = 0;
  header.style.cursor = 'move';
  header.addEventListener('mousedown', e => {
    dragX = e.clientX - panel.offsetLeft;
    dragY = e.clientY - panel.offsetTop;
    function move(ev) {
      panel.style.right = 'auto';
      panel.style.left = (ev.clientX - dragX) + 'px';
      panel.style.top  = (ev.clientY - dragY) + 'px';
    }
    function up() {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
    }
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
  });

  /* ── Init ──────────────────────────────────────────────────────────────── */
  function rebuildPanel() {
    // Update all slider values
    panel.querySelector('#ocp-speed').value  = S.speed;
    panel.querySelector('#ocp-speed-val').value = S.speed.toFixed(2);
    panel.querySelector('#ocp-thick').value  = S.thickness;
    panel.querySelector('#ocp-thick-val').value = S.thickness.toFixed(1);
    panel.querySelector('#ocp-hue').value    = S.hueShift;
    panel.querySelector('#ocp-hue-val').value = S.hueShift;
    panel.querySelector('#ocp-sat').value    = S.saturation;
    panel.querySelector('#ocp-sat-val').value = S.saturation;
    panel.querySelector('#ocp-glow').value   = S.glow;
    panel.querySelector('#ocp-glow-val').value = S.glow.toFixed(1);
    panel.querySelector('#ocp-worm').value   = S.wormLength;
    panel.querySelector('#ocp-worm-val').value = S.wormLength;
    panel.querySelector('#ocp-intro-width').value = S.introLogoWidth;
    panel.querySelector('#ocp-intro-width-val').value = S.introLogoWidth;
    panel.querySelector('#ocp-intro-hold').value = S.introHold;
    panel.querySelector('#ocp-intro-hold-val').value = S.introHold;
    panel.querySelector('#ocp-intro-duration').value = S.introDuration;
    panel.querySelector('#ocp-intro-duration-val').value = S.introDuration;
    panel.querySelector('#ocp-intro-backdrop').value = S.introBackdrop;
    panel.querySelector('#ocp-intro-backdrop-val').value = S.introBackdrop;
    panel.querySelector('#ocp-intro-video-scale').value = S.introVideoScale;
    panel.querySelector('#ocp-intro-video-scale-val').value = S.introVideoScale;
    panel.querySelector('#ocp-use-grad').checked = S.useGradient;
    panel.querySelector('#ocp-pause').textContent = S.paused ? 'RESUME' : 'PAUSE';
    // Re-activate toggle buttons
    ['[data-dir]','[data-style]','[data-pos]','[data-blend]'].forEach(sel => {
      panel.querySelectorAll(sel).forEach(btn => {
        const k = Object.keys(btn.dataset)[0];
        const map = { dir:'direction', style:'style', pos:'position', blend:'blend' };
        btn.classList.toggle('ocp-active', btn.dataset[k] === S[map[k]]);
      });
    });
  }

  drawGradient();
  renderStops();
  renderPresets();
  applyCSS();
  setPreview(panelVisible);

  // Redraw gradient on resize
  window.addEventListener('resize', () => { drawGradient(); });

})();
